import { useCookieUi } from "@/contexts/CookieUiContext";
import React, { useState } from "react";
import { Link } from "@/i18n/routing";
import {
  ArrowRightIcon,
  ChevronRightIcon,
  FacebookIcon,
  LinkedinIcon,
  MailIcon,
  MapPinIcon,
  PinIcon as Pinterest,
  TwitterIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useFooterData } from "@/contexts/HomeDataContext";
import { FooterData } from "@/types/homePage";
import { useTranslations, useLocale } from 'next-intl';
import { FooterCTASection } from "./FooterCTASection";
import { usePathname } from 'next/navigation';

export const FooterSection: React.FC = () => {
  const data = useFooterData();
  const t = useTranslations('Footer');
  const { openPreferences } = useCookieUi();
  const locale = useLocale();
  const pathname = usePathname();
  // Check if we're on the home page
  const isHomePage = pathname === `/${locale}` || pathname === `/${locale}/`;

  return (
    <>
      {!isHomePage && <FooterCTASection></FooterCTASection>}
      <footer className="w-full bg-[#05092B] relative">
        <div className="w-full min-h-[480px] relative">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-12 lg:py-14">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 lg:gap-12">
              
              {/* Company Info Section */}
              <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:0ms] flex flex-col space-y-4 md:space-y-6 text-center sm:text-left rtl:sm:text-right">
                <Link 
                  href={`/`}
                  className="flex items-center justify-center sm:justify-start rtl:sm:justify-start space-x-3 rtl:space-x-reverse cursor-pointer group"
                >
                  <img
                    className="w-[90px] h-[55px] md:w-[110px] md:h-[67px] object-contain transition-transform duration-300 group-hover:scale-105"
                    alt={data.companyInfo.logo.alt}
                    src={data.companyInfo.logo.src}
                  />
                </Link>

                <p className="opacity-70 font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] max-w-[300px] mx-auto sm:mx-0">
                  {t('description')}
                </p>

                <div className="flex justify-center sm:justify-start rtl:sm:justify-start space-x-2.5 rtl:space-x-reverse">
                  {data.socialLinks.map((social: FooterData['socialLinks'][0], index: number) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="icon"
                      className="w-[32px] h-[32px] md:w-[35px] md:h-[35px] rounded-[5px] border-[#ffffff4c] bg-transparent hover:bg-white/10 transition-all duration-300 hover:scale-110"
                      asChild
                    >
                      <a href={social.href} target="_blank" rel="noopener noreferrer">
                        <social.icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                      </a>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Useful Links Section */}
              <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms] flex flex-col space-y-4 md:space-y-6 text-center sm:text-left">
                <h3 className="font-heading-h3 font-[number:var(--heading-h3-font-weight)] text-white text-lg md:text-[length:var(--heading-h3-font-size)] tracking-[var(--heading-h3-letter-spacing)] leading-[var(--heading-h3-line-height)] [font-style:var(--heading-h3-font-style)]">
                  {t('usefulLinks')}
                </h3>

                <nav className="flex flex-col space-y-3 md:space-y-4">
                  <Link
                    href="/about"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.aboutUs')}
                  </Link>
                  <Link
                    href="/contact"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.contactUs')}
                  </Link>
                  <Link
                    href="/services"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.services')}
                  </Link>
                  <Link
                    href="/feedback"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.feedback')}
                  </Link>
                </nav>
              </div>

              {/* Contact Section */}
              <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms] flex flex-col space-y-4 md:space-y-6 text-center sm:text-left">
                <h3 className="font-heading-h3 font-[number:var(--heading-h3-font-weight)] text-white text-lg md:text-[length:var(--heading-h3-font-size)] tracking-[var(--heading-h3-letter-spacing)] leading-[var(--heading-h3-line-height)] [font-style:var(--heading-h3-font-style)]">
                  {t('contact')}
                </h3>

                <div className="flex flex-col space-y-3 md:space-y-4">
                  <a 
                    href={`mailto:${data.contact.email}`}
                    className="opacity-70 font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:opacity-100 transition-all duration-300 hover:underline"
                  >
                    {data.contact.email}
                  </a>

                  <span className="opacity-70 font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)]">
                    {data.contact.address}
                  </span>
                </div>
              </div>

              {/* Legal/Policy Section */}
              <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms] flex flex-col space-y-4 md:space-y-6 text-center sm:text-left">
                <h3 className="font-heading-h3 font-[number:var(--heading-h3-font-weight)] text-white text-lg md:text-[length:var(--heading-h3-font-size)] tracking-[var(--heading-h3-letter-spacing)] leading-[var(--heading-h3-line-height)] [font-style:var(--heading-h3-font-style)]">
                  Legal/Policy
                </h3>

                <nav className="flex flex-col space-y-3 md:space-y-4">
                  <Link
                    href="/legal?doc=LegalNotice"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.legalNotice')}
                  </Link>
                  <Link
                    href="/legal?doc=PrivacyPolicy"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.privacyPolicy')}
                  </Link>
                  <Link
                    href="/legal?doc=TermsOfUse"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.termsConditions')}
                  </Link>
                  <Link
                    href="/legal?doc=CookiePolicy"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.cookiePolicy')}
                  </Link>
                  <button
                    type="button"
                    onClick={openPreferences}
                    className="text-left font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t("links.cookiePreferences")}
                  </button>
                  <Link
                    href="/legal?doc=DataProtection"
                    className="font-body-m font-[number:var(--body-m-font-weight)] text-white text-sm md:text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] hover:text-white/80 transition-all duration-300 hover:translate-x-1 rtl:hover:-translate-x-1"
                  >
                    {t('links.dataProtection')}
                  </Link>
                </nav>
              </div>
            </div>
          </div>

          {/* Decorative Frame SVG */}
          <div className="absolute top-0 right-0 w-[412px] h-[480px] opacity-20 pointer-events-none translate-y-[-1rem] animate-fade-in [--animation-delay:800ms]">
            <img
              src="/svg/footer/frame.svg"
              alt="Decorative Frame"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Footer Bottom */}
          <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:800ms]">
            <Separator className="bg-white/20" />

            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 border-t border-white-20">
              <div className="flex flex-col md:flex-row justify-center items-center gap-2 md:gap-4">
                <span className="opacity-70 font-navigation font-[number:var(--navigation-font-weight)] text-white text-xs md:text-[length:var(--navigation-font-size)] tracking-[var(--navigation-letter-spacing)] leading-[var(--navigation-line-height)] [font-style:var(--navigation-font-style)] text-center">
                  {t('copyright')}
                </span>
                <span className="hidden md:inline opacity-50 text-white">•</span>
                <span className="opacity-60 font-navigation font-[number:var(--navigation-font-weight)] text-white text-xs md:text-[length:var(--navigation-font-size)] tracking-[var(--navigation-letter-spacing)] leading-[var(--navigation-line-height)] [font-style:var(--navigation-font-style)] text-center">
                  {t('companyRegistration')}: {t('registrationNumber')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>    
    </>
  );
};